import { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { sseHost } from '~constants/api';
import { RootState } from 'store';

interface SSEEvent {
  type: string;
  ref: string;
  ts: string;
}

interface UseSSEOptions {
  channels: string[];
  onEvent: (event: SSEEvent) => void;
  enabled?: boolean;
}

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const BACKOFF_MULTIPLIER = 2;

const useSSE = ({ channels, onEvent, enabled = true }: UseSSEOptions) => {
  const sessionToken = useSelector((state: RootState) => state.main.login.sessionToken);
  const [connected, setConnected] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const onEventRef = useRef(onEvent);

  onEventRef.current = onEvent;

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setConnected(false);
  }, []);

  const channelsKey = channels.join(',');

  useEffect(() => {
    if (!enabled || !sseHost || channels.length === 0 || !sessionToken) {
      cleanup();
      return;
    }

    const connect = () => {
      const channelParam = channels.join(',');
      const url = `${sseHost}/stream?token=${encodeURIComponent(sessionToken)}&channels=${encodeURIComponent(channelParam)}`;

      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
        setConnected(true);
      };

      eventSource.onmessage = (messageEvent) => {
        try {
          const data = JSON.parse(messageEvent.data) as SSEEvent;
          onEventRef.current(data);
        } catch {
          // Non-JSON message — ignore
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        eventSourceRef.current = null;
        setConnected(false);

        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * BACKOFF_MULTIPLIER, MAX_RECONNECT_DELAY_MS);

        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return cleanup;
  }, [enabled, channelsKey, cleanup, sessionToken]);

  return { connected };
};

export default useSSE;
